import { cn } from '@tempo-io/sui-class-utils';

import { AddCircle } from '@tempo-io/sui-icons';
import { useState } from 'react';

import { useSwimlaneState } from '@/components/ui/swimlane/context';
import type { ICellProps, TItemID } from '@/components/ui/swimlane/types';
import { buildDraftId } from '@/components/ui/swimlane/utils';

interface IInlineAdd extends ICellProps {
  prevItemID?: TItemID;
  variant: 'divider' | 'card';
}

const variants = {
  divider: {
    containerClass: 'cursor-pointer h-4 -my-4 w-full',
    body: (active: boolean) => (
      <div
        className={cn(
          'relative z-[1000] -my-1.5 h-7',
          'before:content-[""] before:absolute before:left-0 before:right-0 before:top-1/2',
          'before:border-t-2 before:border-[#1D1D1B]',
          'after:content-[""] after:absolute after:left-0 after:right-0 after:top-1/2',
          'after:border-t-2 after:border-[#1D1D1B]',
          'flex items-center justify-center',
          'transition-opacity duration-200',
          active ? 'opacity-100' : 'hidden opacity-0',
        )}
        data-testid="inline-add-divider-body"
      >
        <AddCircle size={30} className="relative z-10 bg-foreground text-background rounded-full" />
      </div>
    ),
  },
  card: {
    containerClass: 'cursor-pointer flex w-full h-[215px]',
    body: (active: boolean) => (
      <div
        className={cn(
          'flex items-center justify-center w-full',
          'bg-gray-100 border border-white rounded-lg',
          'transition-opacity duration-200',
          active ? 'opacity-100' : 'hidden opacity-0',
        )}
        data-testid="inline-add-card-body"
      >
        <span className="text-sm font-normal leading-5 capitalize text-gray-900">+ Add Item</span>
      </div>
    ),
  },
};

export function InlineAdd({ variant, rowID, columnID, prevItemID }: IInlineAdd) {
  const [active, setIsActive] = useState(false);
  const handleActive = (isActive: boolean) => () => setIsActive(isActive);
  const { addCardDraft } = useSwimlaneState();
  const draftId = buildDraftId({ rowID, columnID, itemID: prevItemID });

  const variantConfig = variants[variant];

  return (
    <button
      type="button"
      className={variantConfig.containerClass}
      aria-label="Create Item"
      onBlur={handleActive(false)}
      onClick={() => addCardDraft(draftId)}
      onFocus={handleActive(true)}
      onMouseEnter={handleActive(true)}
      onMouseLeave={handleActive(false)}
    >
      {variantConfig.body(active)}
    </button>
  );
}
